// theme.js
// Central design tokens for the TARTIS site. Read via styled-components'
// ThemeProvider (wired in src/context/ThemeModeContext.jsx) — e.g.
// `color: ${({ theme }) => theme.colors.text.primary};`
//
// Two theme objects share the same shape (lightTheme/darkTheme) so any
// component reading `theme.colors.*` automatically adapts. `brand.*` colors
// are brand-identity constants and intentionally identical in both — they're
// tied to fixed accent tiles, not to the page's light/dark surface colors.
// `text.hero` DOES vary per mode: the hero background is itself a fixed
// light/dark wallpaper image swapped by mode, so its text needs the
// opposite-contrast color in each.
//
// Font mapping reference (see src/index.css for the @font-face rules):
//   Lato       -> fuenteRegular (400) / fuenteNegrita (700) / fuenteSemi (700, same file as fuenteNegrita)
//   Montserrat -> fuenteRegularMont (400) / fuenteSemiMont (600) / fuenteNegritaMont (700)
//   Roboto Mono-> fuenteRegularRoboto (400) / fuenteNegritaRoboto (700)
import { sizes } from './utils/breakpoints'

const brand = {
  green: '#a9ddbf',
  dark: '#22252a',
  darkAlt: '#44494e',
}

const fonts = {
  heading: "'fuenteNegrita'",
  headingAlt: "'fuenteSemi'",
  body: "'fuenteRegular'",
  accentBold: "'fuenteNegritaMont'",
  accentSemi: "'fuenteSemiMont'",
  accentRegular: "'fuenteRegularMont'",
  mono: "'fuenteRegularRoboto'",
  monoBold: "'fuenteNegritaRoboto'",
}

export const lightTheme = {
  mode: 'light',
  colors: {
    brand,
    text: {
      primary: '#272727',
      secondary: '#55585c',
      muted: '#8a8a8a',
      onDark: '#ffffff',
      hero: '#0b0f1d',
      strong: '#000000',
      accent: brand.dark,
      navActive: brand.darkAlt,
    },
    background: {
      page: '#fafafa',
      card: '#ffffff',
      navbar: 'rgba(255, 255, 255, 0.9)',
    },
    border: {
      subtle: '#ececec',
      input: '#d9d9d9',
      focus: brand.darkAlt,
    },
    feedback: {
      success: '#1f6d3f',
      error: '#a4302a',
    },
  },
  shadows: {
    card: '0 4px 24px rgba(0, 0, 0, 0.06)',
    cardHover: '0 12px 32px rgba(0, 0, 0, 0.1)',
    navbar: '0px 0px 7px 0px rgba(123, 123, 123, 0.25)',
  },
  fonts,
  breakpoints: sizes,
}

export const darkTheme = {
  mode: 'dark',
  colors: {
    brand,
    text: {
      primary: '#f2f2f2',
      secondary: '#b5b8bc',
      muted: '#83878c',
      onDark: '#ffffff',
      hero: '#f5f6f7',
      strong: '#ffffff',
      accent: brand.green,
      navActive: brand.green,
    },
    background: {
      page: '#1b1d21',
      card: '#25272c',
      navbar: 'rgba(27, 29, 33, 0.85)',
    },
    border: {
      subtle: 'rgba(255, 255, 255, 0.08)',
      input: 'rgba(255, 255, 255, 0.24)',
      focus: brand.green,
    },
    feedback: {
      success: '#4ade80',
      error: '#f87171',
    },
  },
  shadows: {
    card: '0 4px 24px rgba(0, 0, 0, 0.45)',
    cardHover: '0 12px 32px rgba(0, 0, 0, 0.55)',
    navbar: '0px 0px 7px 0px rgba(0, 0, 0, 0.5)',
  },
  fonts,
  breakpoints: sizes,
}

export const theme = lightTheme

export default theme
